/**
 * The place row: a tile, a name, and what the place is.
 *
 * One component for a row in this list, because the list has three shapes — grouped by area, grouped by
 * kind, and flat — and a row that looked different in each would make the same place a different thing
 * depending on which chip was lit.
 *
 * **The tile is the design, not a photo placeholder.** Nothing on a place carries a photograph: the
 * contract has no image field, and the media routes hold photos the user uploaded to a place's own page.
 * So the left square is a glyph on a tint derived from what the place is — which is what a user with no
 * photographs should get, rather than a grey box announcing a missing image. If a cover ever arrives it
 * fills this same square, and nothing else on the row moves.
 *
 * The tint carries no meaning on its own: the label is beside it in text. Its whole job is to let a
 * column of rows be scanned for "somewhere to sleep" and "something to eat" without reading every line,
 * which is why there are three families rather than one per category — see the tokens' own note.
 */

import type { ReactElement, ReactNode } from "react";
import { Link } from "react-router-dom";

import type { PlaceCategory } from "@trippool/contracts";

import { categoryOf, copy } from "../copy.ts";
import { groupOf } from "../lib/placeGroups.ts";

/** What the card needs from a place. Structural, so both the list's rows and a future caller can pass one. */
export interface PlaceCardPlace {
  id: string;
  name: string;
  categories: readonly PlaceCategory[];
  address: string | null;
  areas: readonly { id: string; name: string }[];
  location: { lat: number; lon: number } | null;
}

/**
 * The three tag families, as literal class names.
 *
 * Literal because the stylesheet test scans this file's strings for the classes it styles: a class built
 * by interpolation is a class nobody can search for, and the gate is right to call that dead.
 */
const TAG_TINT = {
  blue: "place-card__tag--blue",
  warm: "place-card__tag--warm",
  plain: "place-card__tag--plain",
} as const;

/**
 * Which family a category belongs to.
 *
 * A bed is blue and anything you eat or drink is warm; everything else takes the neutral default. Two
 * families and a default is a decision about restraint — a pastel per category reads as decoration and
 * stops being a signal, and this palette has one accent for a reason.
 */
/** The same families on the tile, as a second map: one is a tag's class and one is a square's. */
const TILE_TINT = {
  blue: "place-card__tile--blue",
  warm: "place-card__tile--warm",
  plain: "place-card__tile--plain",
} as const;

type TintFamily = keyof typeof TILE_TINT;

function tintOf(category: PlaceCategory): TintFamily {
  if (category === "stay") return "blue";
  if (category === "cafe" || category === "restaurant" || category === "dessert") return "warm";
  return "plain";
}

/**
 * The tile's tint, for a list that draws the same square without this card.
 *
 * Exported rather than copied: the arrange step gives every place it can see the same tinted square, and a
 * second `if (category === "stay")` written there would be a rule that agrees with this one until somebody
 * changes one of them. Which categories are blue and which are warm is a decision about restraint, not about
 * the row it lands in.
 */
export function tileTintOf(categories: readonly PlaceCategory[]): string {
  return TILE_TINT[tintOf(categories[0] ?? "other")];
}

export interface PlaceCardProps {
  place: PlaceCardPlace;
  /** The ⋯ menu and anything else the screen owns, placed at the row's trailing edge. */
  actions?: ReactNode;
  /**
   * Whether to print the area tags.
   *
   * False inside an area group, where the section heading already says it: a tag that repeats the
   * heading above it is noise in the one place the row is most crowded.
   */
  showAreas?: boolean;
  isMenuOpen?: boolean;
}

export function PlaceCard(props: PlaceCardProps): ReactElement {
  const { place } = props;
  /*
   * The place's **own** first category decides the glyph, and the section is the fallback.
   *
   * Measured against a real list: four restaurants in a row all drew the same bowl, because the
   * section they share is "Ăn uống" — so the tile was answering "what kind of thing is this" with the
   * coarsest possible answer. A dessert shop is filed under that section too, and a cake is a different
   * thing to scan for than a bowl of noodles. The group's glyph still covers a place with no category at
   * all, which is the case it was written for.
   */
  const glyph = place.categories[0] === undefined
    ? groupOf(place.categories).glyph
    : categoryOf(place.categories[0]).glyph;
  const located = place.location !== null;
  const where = place.address ?? place.areas[0]?.name ?? copy.places.noAddress;

  return (
    <article className={props.isMenuOpen ? "place-card place-card--menu-open" : "place-card"}>
      {/*
        The tile is a link and is hidden from assistive technology: the name below is the link a screen
        reader needs, and hearing the same destination twice per row is the cost of a bigger touch target
        that a sighted user gets for free.
      */}
      <Link
        className={`place-card__tile ${tileTintOf(place.categories)}`}
        to={`/places/${place.id}`}
        tabIndex={-1}
        aria-hidden="true"
      >
        <span className="place-card__glyph">{glyph}</span>
      </Link>

      <div className="place-card__body">
        <Link className="place-card__name" to={`/places/${place.id}`}>
          {place.name}
        </Link>

        <p className="place-card__where">
          {/*
            The located state is a mark beside the address rather than a badge of its own.

            A row that says "đã định vị" on every place it can route is a row that says nothing: it is the
            normal case. The absence is the fact worth a line, and it keeps the row to two lines.
          */}
          <span
            className={located ? "place-card__mark" : "place-card__mark place-card__mark--missing"}
            aria-hidden="true"
          >
            {located ? "📍" : "⚠"}
          </span>
          <span className="place-card__where-text">{where}</span>
          {!located && <span className="place-card__flag">{copy.places.notLocated}</span>}
        </p>

        <div className="place-card__tags">
          {place.categories.length === 0 ? (
            <span className={`place-card__tag ${TAG_TINT.plain}`}>{copy.places.noCategory}</span>
          ) : (
            place.categories.map((category) => (
              <span key={category} className={`place-card__tag ${TAG_TINT[tintOf(category)]}`}>
                {categoryOf(category).label}
              </span>
            ))
          )}
          {props.showAreas !== false &&
            place.areas.map((area) => (
              <span key={area.id} className={`place-card__tag ${TAG_TINT.plain}`}>
                {area.name}
              </span>
            ))}
        </div>
      </div>

      {props.actions !== undefined && <div className="place-card__actions">{props.actions}</div>}
    </article>
  );
}
